import React from 'react';
import { Product } from 'bootstrap/data/product/Product';
import { useTranslation } from 'react-i18next';
import { PlanSelectType } from 'subscription/singleproduct/components/plansection/PlanSection.constants';
import { usePlanSectionLogic } from 'subscription/singleproduct/components/plansection/usePlanSection.hook';
import { useFeatures } from 'useFeatures';
import StarIcon from 'assets/livenicons/ic_star.svg';
import DailyPriceIcon from 'assets/livenicons/daily_price.svg';
import styles from './styles.module.scss';

type Props = {
  product: Product;
  currencyCode?: string;
  label?: string;
  isSelected: boolean;
  type: PlanSelectType;
  disabled?: boolean;
  isRenewPriceVisible?: boolean;
  isManualPrice?: boolean;
  isManualTerm?: boolean;
  onClickHandler?: () => void;
};

export const PlanSection: React.FC<Props> = ({
  product,
  currencyCode,
  label,
  isSelected,
  type,
  disabled,
  isRenewPriceVisible,
  isManualTerm,
  isManualPrice,
}) => {
  const { t } = useTranslation();
  const { currentDailyFormattedProductPrice, onClickHandler } =
    usePlanSectionLogic({
      disabled,
      product,
      currencyCode,
    });

  const { isLivenOnboardingFlow } = useFeatures();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [currencySign, firstPriceSymbol, point, ...otherPriceSymbols] =
    currentDailyFormattedProductPrice?.split('') || [];

  const renderLabel = () =>
    label && (
      <div className={styles.label}>
        <img src={StarIcon} alt="" />
        {label.toUpperCase()}
      </div>
    );

  const renderDiscount = () => (
    <div className={styles.discount}>
      {isRenewPriceVisible && (
        <span className={`${styles.old} ${isSelected ? styles.selected : ''}`}>
          <del>{product.getFormattedRenewsPrice(currencyCode)}</del>
        </span>
      )}
      <span className={`${styles.new} ${isSelected ? styles.selected : ''}`}>
        {type === PlanSelectType.WEEK
          ? product.getFormattedWeekPrice(currencyCode)
          : product.getFormattedPrice(currencyCode)}
      </span>
    </div>
  );

  const renderPrice = () => (
    <div className={styles.priceWrapper}>
      <del className={styles.oldDailyPrice}>
        {product.getPlanSectionFormattedDailyPrice(
          currencyCode,
          type === PlanSelectType.WEEK,
        )}
      </del>

      <img src={DailyPriceIcon} alt="" />
      <div className={styles.price}>
        <div className={styles.currency}>
          <span className={styles.currencySymbol}>{currencySign}</span>
        </div>
        <span className={styles.firstSymbol}>
          {isManualPrice ? '1' : firstPriceSymbol}
        </span>
        <div className={styles.otherPrice}>
          <span className={styles.otherPriceSymbols}>
            {isManualPrice ? '50' : otherPriceSymbols.join('')}
          </span>
          <span
            className={`${styles.priceLabel} ${
              isSelected ? styles.selected : ''
            }`}
          >
            per day
          </span>
        </div>
      </div>
    </div>
  );

  const renderTerm = () => (
    <div className={styles.term}>
      {isManualTerm
        ? '7-DAY PLAN'
        : isLivenOnboardingFlow
        ? `${product.getPlanSectionTerm(t, true, true)} PLAN`
        : t('paymentPlanDuration', {
            duration: product.getPlanSectionTerm(t, false, true),
          })}
      {renderDiscount()}
    </div>
  );

  return (
    <div
      className={`
        ${styles.container}
        ${isLivenOnboardingFlow ? styles.liven : ''}
         ${isSelected ? styles.selected : ''} 
         ${styles.multiple}
      `}
      onClick={() => onClickHandler(type)}
    >
      {renderLabel()}
      <div className={styles.content}>
        {renderTerm()}
        {renderPrice()}
      </div>
    </div>
  );
};
